import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeAltEndpoints,
  READY_FAILURES_TO_SHOW_BANNER,
  READY_PROBE_INTERVAL_MS,
  READY_PROBE_TIMEOUT_MS,
  readinessFailuresAfter,
} from '../lib/altEndpoint';

interface Props {
  endpoints: string[];
}

export default function AltEndpointBanner({ endpoints }: Props) {
  const candidate = normalizeAltEndpoints(endpoints, window.location.origin)[0] ?? null;
  if (!candidate) return null;
  return <BannerProbe key={candidate} alternateUrl={candidate} />;
}

function BannerProbe({ alternateUrl }: { alternateUrl: string }) {
  const { t } = useTranslation('misc');
  const [failures, setFailures] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    let probing = false;
    let timer: number | null = null;
    const probe = async () => {
      if (probing) return;
      probing = true;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), READY_PROBE_TIMEOUT_MS);
      let status: number | null = null;
      try {
        const response = await fetch('/readyz', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        });
        status = response.status;
      } catch {
        // Таймаут и сетевая ошибка считаются одной неудачной проверкой.
      } finally {
        window.clearTimeout(timeout);
        probing = false;
      }
      if (!active) return;
      if (status !== null && status < 500) setDismissed(false);
      setFailures((previous) => readinessFailuresAfter(previous, status));
      timer = window.setTimeout(() => { void probe(); }, READY_PROBE_INTERVAL_MS);
    };

    void probe();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [alternateUrl]);

  if (failures < READY_FAILURES_TO_SHOW_BANNER || dismissed) return null;

  return (
    <div className="alt-endpoint-banner" role="status" aria-live="polite">
      <span className="alt-endpoint-banner-text">
        {t('altEndpointUnavailable')}{' '}
        <a className="alt-endpoint-banner-link" href={alternateUrl}>{alternateUrl}</a>.
        {' '}{t('altEndpointSignInAgain')}
      </span>
      <button
        className="alt-endpoint-banner-close"
        type="button"
        aria-label={t('altEndpointClose')}
        onClick={() => setDismissed(true)}
      >×</button>
    </div>
  );
}
